import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SharedVideo } from '@vc/views/panels/VideoSharePanel'
import type { SharedDocument } from '@vc/views/panels/DocumentSharePanel'

/**
 * Files shared into the current meeting.
 *
 * Persisted, because a refresh mid-meeting used to empty the panel: the lists
 * lived in memory only, so reloading lost every file you had just uploaded even
 * though the bytes were sitting safely on the server.
 *
 * Scoped to one room. The stored blob carries the roomId it belongs to and is
 * cleared when the room changes, so walking into a different meeting never
 * shows the previous meeting's files. Entries whose URL is a leftover `blob:`
 * (from before uploads existed, or a share that failed to upload) are dropped —
 * those handles die with the tab that made them, so restoring one would only
 * ever produce a permanently broken row.
 */

type MeetingMediaShareState = {
  /** Room these lists belong to. Null before a meeting is entered. */
  roomId: string | null
  sharedVideos: SharedVideo[]
  sharedDocuments: SharedDocument[]
  /** Bind the store to a room, clearing anything held for a different one. */
  setRoomId: (roomId: string | null) => void
  setSharedVideos: (videos: SharedVideo[] | ((prev: SharedVideo[]) => SharedVideo[])) => void
  setSharedDocuments: (
    docs: SharedDocument[] | ((prev: SharedDocument[]) => SharedDocument[]),
  ) => void
  addSharedVideo: (video: SharedVideo) => void
  removeSharedVideo: (videoId: string) => void
  addSharedDocument: (doc: SharedDocument) => void
  removeSharedDocument: (documentId: string) => void
}

type PersistedShare = {
  roomId: string | null
  sharedVideos: SharedVideo[]
  sharedDocuments: SharedDocument[]
}

/** Will this URL still resolve on a future page load? */
function durable(url: string | undefined): boolean {
  if (!url) return false
  return !url.startsWith('blob:') && !url.startsWith('data:')
}

export const useMeetingMediaShareStore = create<MeetingMediaShareState>()(
  persist(
    (set, get) => ({
      roomId: null,
      sharedVideos: [],
      sharedDocuments: [],
      setRoomId: (roomId) =>
        set((s) =>
          s.roomId === roomId
            ? { roomId }
            : // A different meeting starts clean rather than inheriting.
              { roomId, sharedVideos: [], sharedDocuments: [] },
        ),
      setSharedVideos: (videos) =>
        set({
          sharedVideos: typeof videos === 'function' ? videos(get().sharedVideos) : videos,
        }),
      setSharedDocuments: (docs) =>
        set({
          sharedDocuments: typeof docs === 'function' ? docs(get().sharedDocuments) : docs,
        }),
      addSharedVideo: (video) => set((s) => ({ sharedVideos: [...s.sharedVideos, video] })),
      removeSharedVideo: (videoId) =>
        set((s) => ({ sharedVideos: s.sharedVideos.filter((v) => v.id !== videoId) })),
      addSharedDocument: (doc) => set((s) => ({ sharedDocuments: [...s.sharedDocuments, doc] })),
      removeSharedDocument: (documentId) =>
        set((s) => ({
          sharedDocuments: s.sharedDocuments.filter((d) => d.id !== documentId),
        })),
    }),
    {
      name: 'inmeet_meeting_media_share',
      version: 1,
      partialize: (state): PersistedShare => ({
        roomId: state.roomId,
        // Only entries that can be re-fetched next load.
        sharedVideos: state.sharedVideos.filter((v) => durable(v.url)),
        sharedDocuments: state.sharedDocuments.filter((d) => durable(d.url)),
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<PersistedShare>
        return {
          ...current,
          roomId: p.roomId ?? null,
          sharedVideos: (p.sharedVideos ?? []).filter((v) => durable(v.url)),
          sharedDocuments: (p.sharedDocuments ?? []).filter((d) => durable(d.url)),
        }
      },
    },
  ),
)
